// Copyright (c) Bentley Systems
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectOption } from "@itwin/itwinui-react";
import { ConfigManager } from "./config-manager";
import { setErrorState } from "./helper-functions";
import { AppDispatch } from "../app/store";
import { loadingSkeleton, searchEmptyMessage } from "./ComboBoxHelpers";

export type IModel = {
  id: string;
  displayName: string;
};

type IModelApiItem = {
  id: string;
  displayName: string;
};

type IModelsListResponse = {
  iModels?: IModelApiItem[];
  _links?: { next?: { href?: string } };
};

type SingleIModelResponse = {
  iModel?: IModelApiItem;
};

type UseIModelsProps = {
  iTwinId: string | null;
  currentIModelId: string | null;
  accessToken: string;
  dispatch: AppDispatch;
};

type UseIModelsReturn = {
  iModels: IModel[];
  isLoading: boolean;
  selectOptions: SelectOption<string>[];
  emptyContent: React.JSX.Element | string;
  searchString: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleIModelSelect: (id: string) => void;
  fetchNextIModels: () => void;
  hasMore: boolean;
};

export const useIModels = ({
  iTwinId,
  currentIModelId,
  accessToken,
  dispatch,
}: UseIModelsProps): UseIModelsReturn => {
  const iModelsPerPage = 50;

  const accessTokenRef = useRef(accessToken);
  useEffect(() => { accessTokenRef.current = accessToken; }, [accessToken]);

  const currentIModelIdRef = useRef(currentIModelId);
  useEffect(() => { currentIModelIdRef.current = currentIModelId; }, [currentIModelId]);

  const [iModels, setIModels] = useState<IModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nextLink, setNextLink] = useState<string | null>(null);
  const [searchString, setSearchString] = useState("");
  const fetchNextAbortRef = useRef<AbortController | null>(null);

  const fetchSingleIModel = useCallback(async (iModelId: string, signal?: AbortSignal) => {
    const url = `${ConfigManager.iTwinPlatformUrl}/imodels/${encodeURIComponent(iModelId)}`;
    const runId = crypto.randomUUID();
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: accessTokenRef.current,
          Accept: "application/vnd.bentley.itwin-platform.v2+json",
          RunId: runId,
        },
        signal,
      });
      if (!response.ok) return;
      const data: SingleIModelResponse = await response.json();
      if (data.iModel) {
        const model: IModel = {
          id: data.iModel.id,
          displayName: data.iModel.displayName,
        };
        setIModels((prev) =>
          prev.some((m) => m.id === model.id) ? prev : [model, ...prev],
        );
      }
    } catch {
      // Silently ignore — the dropdown still works without the current item pre-selected
    }
  }, []);

  const fetchIModels = useCallback(async (search?: string, signal?: AbortSignal) => {
    if (!iTwinId) return;

    setNextLink(null);
    let url = `${ConfigManager.iTwinPlatformUrl}/imodels/?iTwinId=${encodeURIComponent(iTwinId)}&$top=${iModelsPerPage}&$orderBy=name`;
    if (search && search.trim().length > 0) {
      url += `&$search=${encodeURIComponent(search.trim())}`;
    }

    const runId = crypto.randomUUID();
    setIsLoading(true);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: accessTokenRef.current,
          Accept: "application/vnd.bentley.itwin-platform.v2+json",
          RunId: runId,
        },
        signal,
      });
      if (!response.ok) {
        setErrorState(response.status, dispatch, runId, false);
        return;
      }
      const data: IModelsListResponse = await response.json();
      const models: IModel[] = (data.iModels ?? []).map((m) => ({
        id: m.id,
        displayName: m.displayName,
      }));
      setIModels(models);
      setNextLink(data._links?.next?.href ?? null);

      // If current iModel is not in the fetched list, fetch it individually
      if (
        currentIModelIdRef.current &&
        !search &&
        !models.some((m) => m.id === currentIModelIdRef.current)
      ) {
        void fetchSingleIModel(currentIModelIdRef.current, signal);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        setErrorState(500, dispatch, runId, true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [iTwinId, fetchSingleIModel, dispatch]);

  const fetchNextIModels = useCallback(() => {
    if (!nextLink) return;

    // Abort any previous "load more" fetch
    fetchNextAbortRef.current?.abort();
    const abortController = new AbortController();
    fetchNextAbortRef.current = abortController;

    const runId = crypto.randomUUID();
    setIsLoading(true);
    fetch(nextLink, {
      method: "GET",
      headers: {
        Authorization: accessTokenRef.current,
        Accept: "application/vnd.bentley.itwin-platform.v2+json",
        RunId: runId,
      },
      signal: abortController.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }
        return response.json();
      })
      .then((data: IModelsListResponse | undefined) => {
        if (!data) return;
        const models: IModel[] = (data.iModels ?? []).map((m) => ({
          id: m.id,
          displayName: m.displayName,
        }));
        setIModels((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newModels = models.filter((m) => !existingIds.has(m.id));
          return [...prev, ...newModels];
        });
        setNextLink(data._links?.next?.href ?? null);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      })
      .finally(() => setIsLoading(false));
  }, [nextLink, dispatch]);

  // When ComboBox selects an item it auto-fills the input with the label,
  // which fires inputProps.onChange.  We skip that event so the search state
  // stays unchanged and the dropdown keeps showing the full result list.
  const justSelectedRef = useRef(false);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    setSearchString(e.target.value);
  }, []);

  // Selection handler
  const handleIModelSelect = useCallback((_id: string) => {
    justSelectedRef.current = true;
  }, []);

  // Single effect for both initial load and debounced search
  useEffect(() => {
    if (!iTwinId) {
      setIModels([]);
      return;
    }
    const trimmed = searchString.trim();

    // Empty → load unfiltered first page immediately.
    // 1-2 chars → clear results, show hint.
    // 3+ chars → debounced server search.
    if (trimmed.length > 0 && trimmed.length < 3) {
      setIModels([]);
      setNextLink(null);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    const searchArg = trimmed.length >= 3 ? trimmed : undefined;

    // Show skeleton immediately so "No options found" doesn't flash
    // during the debounce delay.
    if (searchArg) { setIsLoading(true); }

    const timer = setTimeout(() => {
      void fetchIModels(searchArg, abortController.signal);
    }, searchString === "" ? 0 : 400);

    return () => {
      clearTimeout(timer);
      abortController.abort();
      fetchNextAbortRef.current?.abort();
    };
  }, [searchString, iTwinId, fetchIModels]);

  const selectOptions = useMemo(
    () => iModels.map((m): SelectOption<string> => ({
      value: m.id,
      label: m.displayName,
    })),
    [iModels],
  );

  const emptyContent = useMemo(() => {
    if (isLoading) { return loadingSkeleton(3); }
    return searchEmptyMessage(searchString, 3, "No iModels found");
  }, [isLoading, searchString]);

  return {
    iModels,
    isLoading,
    selectOptions,
    emptyContent,
    searchString,
    handleSearchChange,
    handleIModelSelect,
    fetchNextIModels,
    hasMore: nextLink !== null,
  };
};
